import React, { useEffect, useRef, useState } from 'react';
import type { ApprovalDto, RejectReasonCode } from '../services/MendsVisionService';
import { Markdown } from './Markdown';
import {
  REJECT_REASONS,
  ReasonText,
  actionKindLabel,
  explainApproval,
  expiryText,
  isOpenStatus,
  toneClass,
} from '../lib/approvalLabels';
import { commitmentQuote, commitmentSendsAt, splitEvidence, suggestionFromQuestion } from '../lib/approvalContent';

export interface ApprovalCardProps {
  item: ApprovalDto;
  nowMs: number;
  highlight: boolean;
  busy: boolean;
  editing: boolean;
  editBody: string;
  /** Último bloqueio do Ok (409 CONTEXT_UNVERIFIED): o cartão voltou a pendente e explica por quê. */
  blockedNotice?: ReasonText | null;
  cardRef?: (el: HTMLElement | null) => void;
  onEditBodyChange: (value: string) => void;
  /** Decisão de desconto: percentual final em edição (texto do campo). */
  discountPercent?: string;
  onDiscountPercentChange?: (value: string) => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onApprove: () => void;
  /** Sem código = rejeitar sem motivo. */
  onReject: (code?: RejectReasonCode) => void;
  onUndo: () => void;
  /** Resolve com a mensagem de erro a mostrar no cartão, ou null quando a resposta foi aceita. */
  onAnswer: (answer: string) => Promise<string | null>;
  onOpenChat: () => void;
}

/** Cartão da fila: o que a IA propõe, por que está assim e qual é a ação seguinte. */
export const ApprovalCard: React.FC<ApprovalCardProps> = ({
  item,
  nowMs,
  highlight,
  busy,
  editing,
  editBody,
  blockedNotice,
  cardRef,
  onEditBodyChange,
  discountPercent,
  onDiscountPercentChange,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onApprove,
  onReject,
  onUndo,
  onAnswer,
  onOpenChat,
}) => {
  const titleId = `mai-approval-title-${item.id}`;
  const status = (item.status || '').trim().toLowerCase();
  const open = isOpenStatus(status);
  const needsInfo = status === 'needs_info';
  const executable = status === 'pending' || status === 'edited';
  const commitment = (item.actionKind || '').trim().toLowerCase() === 'execute_commitment';
  const explained = explainApproval(item);
  const { text, evidence } = splitEvidence(item.body);
  const quote = commitment ? commitmentQuote(item.suggestedAction) : null;
  const sendsAt = commitment && open ? commitmentSendsAt(item.suggestedAction) : null;
  const reason = (open && blockedNotice) || explained.reason;
  const expiry = open ? expiryText(item.expiresAt, nowMs) : null;
  const canUndo = status === 'approved' && item.reversible !== false;

  const [choosingReason, setChoosingReason] = useState(false);
  const [answering, setAnswering] = useState(false);
  const rejectBtnRef = useRef<HTMLButtonElement | null>(null);
  const firstChipRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (choosingReason) firstChipRef.current?.focus();
  }, [choosingReason]);

  const closeReasons = () => {
    setChoosingReason(false);
    rejectBtnRef.current?.focus();
  };

  return (
    <article
      ref={cardRef}
      aria-labelledby={titleId}
      className={`mai-approval-card ${highlight ? 'is-highlight' : ''}`}
    >
      <div className="mai-approval-meta">
        <span className={`mai-pill ${toneClass(explained.status.tone)}`}>{explained.status.label}</span>
        {item.contactName && <span className="mai-pill">{item.contactName}</span>}
        <span className="mai-pill">{actionKindLabel(item.actionKind)}</span>
      </div>
      <p className="mai-card-title" id={titleId}>{item.title}</p>
      {sendsAt && <p className="mai-approval-when">Sai em {sendsAt}</p>}

      {needsInfo && (
        <NeedsInfoForm item={item} disabled={busy} onAnswer={onAnswer} onSendingChange={setAnswering} />
      )}

      {editing ? (
        <>
          {(item.actionKind || '').trim().toLowerCase() === 'discount_decision' && onDiscountPercentChange && (
            <>
              <label className="mai-approval-label" htmlFor={`mai-approval-pct-${item.id}`}>
                Percentual aprovado (%) — o pedido original fica registrado
              </label>
              <input
                id={`mai-approval-pct-${item.id}`}
                inputMode="decimal"
                value={discountPercent ?? ''}
                onChange={(e) => onDiscountPercentChange(e.target.value)}
                className="mai-approval-edit"
                style={{ minHeight: 0 }}
              />
            </>
          )}
          <label className="mai-approval-label" htmlFor={`mai-approval-edit-${item.id}`}>Texto da mensagem</label>
          <textarea
            id={`mai-approval-edit-${item.id}`}
            value={editBody}
            onChange={(e) => onEditBodyChange(e.target.value)}
            className="mai-approval-edit"
          />
        </>
      ) : commitment ? (
        <>
          <div className="mai-approval-label">Mensagem que vai sair</div>
          <div className="mai-card-detail mai-approval-body mai-approval-draft">
            <Markdown text={text} />
          </div>
        </>
      ) : (
        text && (
          <div className="mai-card-detail mai-approval-body">
            <Markdown text={text} />
          </div>
        )
      )}

      {quote && (
        <>
          <div className="mai-approval-label">O que o cliente disse</div>
          <blockquote className="mai-approval-quote">“{quote}”</blockquote>
        </>
      )}

      {evidence.map((line) => (
        <p key={line} className="mai-approval-evidence">{line}</p>
      ))}

      {!commitment && !needsInfo && item.suggestedAction && (
        <p className="mai-approval-hint">→ {item.suggestedAction}</p>
      )}

      {reason && !needsInfo && (
        <p className="mai-approval-reason">
          <strong>{reason.label}</strong>
          {reason.hint && <span> {reason.hint}</span>}
        </p>
      )}
      {needsInfo && reason && (
        <p className="mai-approval-expiry">{reason.label}</p>
      )}
      {explained.detail && <p className="mai-approval-reason">{explained.detail}</p>}

      {explained.result && (
        <div className="mai-cmd-confirm-ok mai-approval-result">
          <Markdown text={explained.result} />
        </div>
      )}

      {expiry && <p className="mai-approval-expiry">{expiry}</p>}

      <div className="mai-btn-row">
        {executable && (
          <>
            <button className="mai-btn" disabled={busy} onClick={onApprove}>
              {busy ? 'Executando…' : 'Ok'}
            </button>
            {editing ? (
              <>
                <button className="mai-btn mai-btn-soft" disabled={busy} onClick={onSaveEdit}>Salvar</button>
                <button className="mai-btn mai-btn-ghost" disabled={busy} onClick={onCancelEdit}>Cancelar</button>
              </>
            ) : (
              <button className="mai-btn mai-btn-soft" disabled={busy} onClick={onStartEdit}>Editar</button>
            )}
            <button
              ref={rejectBtnRef}
              className="mai-btn mai-btn-ghost"
              disabled={busy}
              aria-expanded={choosingReason}
              onClick={() => setChoosingReason((v) => !v)}
            >
              Rejeitar
            </button>
          </>
        )}
        {needsInfo && (
          <button className="mai-btn mai-btn-ghost" disabled={busy || answering} onClick={() => onReject()}>
            Descartar pergunta
          </button>
        )}
        {canUndo && (
          <button className="mai-btn mai-btn-ghost" disabled={busy} onClick={onUndo}>Desfazer</button>
        )}
        {item.contactId && (
          <button className="mai-btn mai-btn-ghost" onClick={onOpenChat}>Abrir chat</button>
        )}
      </div>

      {executable && choosingReason && (
        <div
          className="mai-approval-reject"
          role="group"
          aria-label="Por que rejeitar?"
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              closeReasons();
            }
          }}
        >
          <span className="mai-approval-label">Por que rejeitar? (opcional)</span>
          <div className="mai-chip-row">
            {REJECT_REASONS.map((r, i) => (
              <button
                key={r.code}
                ref={i === 0 ? firstChipRef : undefined}
                type="button"
                className="mai-chip"
                disabled={busy}
                onClick={() => onReject(r.code)}
              >
                {r.chip}
              </button>
            ))}
            <button type="button" className="mai-chip" disabled={busy} onClick={() => onReject()}>
              Só rejeitar
            </button>
          </div>
        </div>
      )}
    </article>
  );
};

/** Pergunta única da IA: campo curto (Enter envia), chip de sugestão quando seguro e erro visível. */
const NeedsInfoForm: React.FC<{
  item: ApprovalDto;
  disabled: boolean;
  onAnswer: (answer: string) => Promise<string | null>;
  onSendingChange: (sending: boolean) => void;
}> = ({ item, disabled, onAnswer, onSendingChange }) => {
  const [answer, setAnswer] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const inputId = `mai-approval-answer-${item.id}`;
  const errorId = `${inputId}-error`;
  const suggestion = suggestionFromQuestion(item.clarificationQuestion, item.missingSlots);

  useEffect(() => () => {
    mounted.current = false;
    onSendingChange(false);
  }, [onSendingChange]);

  // Erro: o campo volta a ficar ativo e recebe o foco para corrigir a resposta.
  useEffect(() => {
    if (error && !sending) inputRef.current?.focus();
  }, [error, sending]);

  const send = async (value: string) => {
    const body = value.trim();
    if (!body || sending) return;
    setSending(true);
    onSendingChange(true);
    setError(null);
    const err = await onAnswer(body);
    if (!mounted.current) return;
    setSending(false);
    onSendingChange(false);
    if (err) setError(err);
    else setAnswer('');
  };

  const locked = sending || disabled;

  return (
    <div className="mai-approval-question">
      <div className="mai-approval-label">A IA precisa de uma informação</div>
      <p className="mai-approval-question-text">
        {item.clarificationQuestion || 'Falta uma informação para completar esta ação.'}
      </p>
      <form
        className="mai-approval-answer"
        onSubmit={(e) => {
          e.preventDefault();
          void send(answer);
        }}
      >
        <label className="mai-approval-label" htmlFor={inputId}>Sua resposta</label>
        <div className="mai-approval-answer-row">
          <input
            ref={inputRef}
            id={inputId}
            type="text"
            value={answer}
            maxLength={500}
            disabled={locked}
            aria-invalid={!!error}
            aria-describedby={error ? errorId : undefined}
            onChange={(e) => setAnswer(e.target.value)}
            placeholder="Responda em uma frase"
            autoComplete="off"
          />
          <button type="submit" className="mai-btn" disabled={locked || !answer.trim()}>
            {sending ? 'Enviando…' : 'Responder'}
          </button>
        </div>
      </form>
      {suggestion && (
        <div className="mai-chip-row">
          <button type="button" className="mai-chip" disabled={locked} onClick={() => void send(suggestion.answer)}>
            {suggestion.chip}
          </button>
        </div>
      )}
      {error && <p id={errorId} className="mai-form-error" role="alert">{error}</p>}
    </div>
  );
};
